import React, { useRef, useState, useEffect } from 'react';
import {
  makeStyles,
  Card,
  CardContent,
  Box,
  Button,
  TextField,
  FormHelperText,
  Typography
} from '@material-ui/core';
import { useParams } from 'react-router';
import Page from 'src/components/Page';
import ThreadDetails from './ThreadDetails';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { addMessage } from 'src/actions/channelWeb';
import { useDispatch, useSelector } from 'react-redux';
import LoadingScreen from 'src/components/LoadingScreen';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'white',
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    position: 'relative'
  },
  card: {
    height: '100vh',
    overflow: 'visible',
    display: 'flex',
    position: 'relative',
    borderRadius: '20px',
    '& > *': {
      flexGrow: 1,
      flexBasis: '50%',
      width: '50%'
    }
  },
  content: {
    padding: theme.spacing(5.5, 3, 3, 3)
  },
  container: {
    display: 'Flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  actions: {
    float: 'right'
  },
  formButton: props => ({
    backgroundColor: props.bgStyle,
    color: props.colorStyle,
  }),
}));

const messagesSelector = (state, userId) => {
  let threads = state.chat.threads.allKeys.map(
    key => state.chat.threads.byKey[key]
  );
  const filtered = threads.filter(e =>
    [userId, userId.split(':')[0]].includes(e.user_id)
  );
  let messages = [];
  if (filtered.length) {
    messages = state.chat.messages.byThreadId[filtered[0].id];
  }

  return messages;
};

function ChatView({ className, onSubmitSuccess, disabled, ...rest }) {
  const pageRef = useRef(null);
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(document.location.search);
  const { config } = JSON.parse(searchParams.get('options'));
  let bgStyle = config.bgColor || '#242b39';
  let colorStyle = config.textColor || '#fff';
  const classes = useStyles({bgStyle,colorStyle});
  const { t } = useTranslation('common');
  const { userId, botId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const messages = useSelector(state =>
    messagesSelector(state, `${userId}:web:${botId}`)
  );

  const formChatEnabled = searchParams.get('formChatEnabled') === 'true';

  let messageHistory = messages.length > 0 ? true : null;

  const onAdd = async (botId, userId, user) => {
    dispatch(
      addMessage(botId, userId, undefined, {
        type: 'text',
        text: config.startMessage || 'Empezar',
        user
      })
    );
  };

  const startChat = async () => {
    setSubmitting(true)
    await onAdd(botId, userId, undefined);
    messageHistory = true;
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 700);
   
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Page className={classes.root} title='Chat' ref={pageRef}>
      {!messageHistory ? (
        formChatEnabled ? (
          <Formik
            initialValues={{
              email: '',
              name: '',
              last_name: ''
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email(t('formWeb.validEmail'))
                .max(255)
                .required(t('formWeb.requiredEmail')),
              name: Yup.string()
                .max(255)
                .required(t('formWeb.requiredName'))
            })}
            onSubmit={async (
              values,
              { setErrors, setStatus }
            ) => {
              try {
                setSubmitting(true)
                const name = values.name;
                const email = values.email;
                const last_name = values.last_name;
                
                await onAdd(botId, userId, { name, email, last_name });
                messageHistory = true;

                onSubmitSuccess();
              } catch (error) {
                const message =
                  (error.response && error.response.data.message) ||
                  'Something went wrong';

                setStatus({ success: false });
                setErrors({ submit: message });
                setSubmitting(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values
            }) => (
              <div className={classes.container}>
                <form
                  noValidate
                  className={clsx(classes.root, className)}
                  onSubmit={handleSubmit}
                  {...rest}
                >
                  <PerfectScrollbar>
                    <Card className={classes.card}>
                      <CardContent className={classes.content}>
                        <Typography variant='h2' color='textPrimary'>
                          {t('formWeb.title')}
                        </Typography>
                        <Typography color='textSecondary'>
                          {t('formWeb.subTitle')}
                        </Typography>
                        <TextField
                          error={Boolean(touched.name && errors.name)}
                          fullWidth
                          autoFocus
                          helperText={touched.name && errors.name}
                          label={t('formWeb.name')}
                          margin='normal'
                          name='name'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type='string'
                          value={values.name}
                          variant='outlined'
                        />
                        <TextField
                          error={Boolean(touched.last_name && errors.last_name)}
                          fullWidth
                          autoFocus
                          helperText={touched.last_name && errors.last_name}
                          label={t('formWeb.lastName')}
                          margin='normal'
                          name='last_name'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type='string'
                          value={values.last_name}
                          variant='outlined'
                        />
                        <TextField
                          error={Boolean(touched.email && errors.email)}
                          fullWidth
                          autoFocus
                          helperText={touched.email && errors.email}
                          label={t('formWeb.email')}
                          margin='normal'
                          name='email'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type='email'
                          value={values.email}
                          variant='outlined'
                        />
                        <Box flexGrow={1} mt={2}>
                          <Button
                          className={classes.formButton}
                            fullWidth
                            size='large'
                            type='submit'
                            variant='contained'
                            disabled={submitting}
                          >
                            {t('formWeb.buttonWeb')}
                          </Button>
                          <Typography
                            style={{ fontSize: '12px', paddingTop: '15px' }}
                            color='textSecondary'
                          >
                            {t('formWeb.disclaimer1')}{' '}
                            <a
                              target='_parent'
                              href='https://smartbotscr.com/politica-de-privacidad'
                            >
                              {t('formWeb.pp')}
                            </a>{' '}
                            {t('formWeb.disclaimer2')}
                            <a
                              target='_parent'
                              href='https://smartbotscr.com/politica-de-privacidad'
                            >
                              {t('formWeb.tyc')}
                            </a>
                            .
                          </Typography>
                          {errors.submit && (
                            <Box mt={3}>
                              <FormHelperText error>
                                {errors.submit}
                              </FormHelperText>
                            </Box>
                          )}
                        </Box>{' '}
                      </CardContent>
                    </Card>
                  </PerfectScrollbar>
                </form>
              </div>
            )}
          </Formik>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              width: '100vw'
            }}
          >
            <Button
              color='primary'
              size='large'
              type='submit'
              variant='contained'
              disabled={submitting}
              onClick={startChat}
              style={{
                width: '50%'
              }}
            >
              {t('startChatButton')}
            </Button>
          </div>
        )
      ) : (
        <ThreadDetails config={config} />
      )}
    </Page>
  );
}

ChatView.propTypes = {
  className: PropTypes.string,
  onSubmitSuccess: PropTypes.func
};

ChatView.defaultProps = {
  onSubmitSuccess: () => {}
};
export default ChatView;
